import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';
import type { UserOrganization } from '@flock-types/auth.types';

/** Group row from GET /groups?manageable=true (subset the rail needs). */
export interface ManageableGroup {
  id: string;
  name: string;
  group_image_url?: string | null;
}

interface GroupsApiResponse {
  success: boolean;
  data: Array<{ id: string; name: string; group_image_url?: string | null }>;
  count: number;
}

const SCOPED_ROSTER_ROLES = ['org_admin', 'campus_pastor', 'ministry_director'] as const;
export type ScopedRosterRole = (typeof SCOPED_ROSTER_ROLES)[number];

/**
 * Highest role that can open the org-level ("All Members") roster —
 * mirrors the backend's GET /roster/scoped gate.
 */
export function scopedRosterRole(membership: UserOrganization): ScopedRosterRole | null {
  for (const role of SCOPED_ROSTER_ROLES) {
    if (membership.roles?.some((r) => r.role === role)) return role;
  }
  return null;
}

/**
 * Whether GET /groups?manageable=true would return anything for this org
 * (group_leader gets the groups they lead; plain members get []).
 */
export function hasManageableScope(membership: UserOrganization): boolean {
  return (
    membership.roles?.some(
      (r) =>
        r.role === 'org_admin' ||
        r.role === 'campus_pastor' ||
        r.role === 'ministry_director' ||
        r.role === 'group_leader'
    ) ?? false
  );
}

/**
 * Groups the user can shepherd beyond their own memberships, per org —
 * org_admin: all org groups; campus_pastor/ministry_director: their scope;
 * group_leader: groups they lead. Each request is pinned to its org via the
 * X-Organization-Id header so the rail can span orgs without rotating the
 * global org context (events calendar pattern).
 */
export function useAllManageableGroups(): {
  byOrg: Record<string, ManageableGroup[]>;
  isLoading: boolean;
} {
  const { organizations } = useAuth();
  const leaderOrgs = useMemo(
    () => organizations.filter((m) => m.status === 'active' && hasManageableScope(m)),
    [organizations]
  );

  return useQueries({
    queries: leaderOrgs.map((membership) => ({
      queryKey: ['manageable-groups', membership.organization_id],
      queryFn: async (): Promise<{ orgId: string; groups: ManageableGroup[] }> => {
        const response = await api.get<GroupsApiResponse>('/groups?manageable=true', {
          headers: { 'X-Organization-Id': membership.organization_id },
        });
        return {
          orgId: membership.organization_id,
          groups: (response.data ?? []).map((g) => ({
            id: g.id,
            name: g.name,
            group_image_url: g.group_image_url ?? null,
          })),
        };
      },
      staleTime: 5 * 60 * 1000,
    })),
    // combine memoizes the merged result so consumers don't re-render on
    // every unrelated query state tick
    combine: (results) => {
      const byOrg: Record<string, ManageableGroup[]> = {};
      for (const result of results) {
        if (result.data) byOrg[result.data.orgId] = result.data.groups;
      }
      return { byOrg, isLoading: results.some((r) => r.isLoading) };
    },
  });
}
